import axios from 'axios';
import * as ActionCreators from './actionCreators';

function applyScaleFactor(element, factor, defaults) {
  if (factor === 1) return;
  // Calculate new dimensions without applying them.
  const newWidth = element.width * factor;
  const newHeight = element.height * factor;
  // Prevent shrinking too small
  if (newWidth < defaults.width || newHeight < defaults.height) return;
  // Apply new dimensions.
  element.setAttribute('width', Math.round(newWidth));
  element.setAttribute('height', Math.round(newHeight));
}

/**
 * Prevents negative results.
 * @param {number} num - x- or y-axis pixel coordinate number.
 */
function positiveNumber(num) {
  if (num && typeof num === 'number') {
    if (num < 0) return 0;
    return Math.round(num);
  }
  return 0;
}

/**
 * Centers crop zone by updating defaults and setting top/left styles on zone.
 * @param {DOM element} zone - the draggable, expandable crop zone (#crop-zone)
 * @param {DOM element} bound - the outer edge of the image to crop (#crop-container)
 * @param {object} defaults - the default coordinates of the zone.
 */
function centerCropZone(zone, bound, defaults) {
  if (bound.offsetHeight > defaults.height) {
    /* eslint-disable */
    defaults.top = positiveNumber(
      /* eslint-enable */
      Math.round(bound.offsetHeight / 2) -
        Math.round(defaults.height * 0.618034),
    ); // Golden Ratio
  }
  if (bound.offsetWidth > defaults.width) {
    /* eslint-disable */
    defaults.left = positiveNumber(
      /* eslint-enable */
      Math.round(bound.offsetWidth / 2) - Math.round(defaults.width / 2),
    );
  }
  zone.style.top = `${defaults.top}px`; // eslint-disable-line
  zone.style.left = `${defaults.left}px`; // eslint-disable-line
}

/**
 * Changes border to dashed when dragging.
 */
function dashBorder(el) {
  const defView = document.defaultView;
  const getStyle =
    defView && defView.getComputedStyle
      ? elem => defView.getComputedStyle(elem, null)
      : elem => elem.currentStyle;
  el.savedBorder = getStyle(el).borderStyle; // eslint-disable-line
  el.style.borderStyle = 'dashed'; // eslint-disable-line
}

/**
 * Moves the handle elements to starting positions.
 * @param {string} className - class of handle elements.
 */
function resetHandlePositions(className) {
  const handles = document.getElementsByClassName(className);
  for (let i = 0; i < handles.length; i += 1) {
    handles[i].style.top = '';
    handles[i].style.left = '';
  }
}

function updateScale(imgEl, cropZone, cropBound, defaults) {
  return dispatch => {
    // Resize the cropper as soon as we have an image to resize.
    if (!imgEl.width && !imgEl.height) {
      return setTimeout(() => {
        dispatch(updateScale(imgEl, cropZone, cropBound, defaults));
      }, 10);
    }

    // Calculate new factor.
    const bodyWidth = document
      .getElementsByTagName('body')[0]
      .getBoundingClientRect().width;
    const bodyHeight = document
      .getElementsByTagName('body')[0]
      .getBoundingClientRect().height;
    const marginPercent = 0.2; // at least 20% margin
    let factor = 1;

    if (imgEl.width > imgEl.height) {
      if (imgEl.width > bodyWidth) {
        factor = bodyWidth / imgEl.width * (1 - 2 * marginPercent); // horizontal
      }
    } else if (imgEl.height + 150 > bodyHeight) {
      factor = bodyHeight / imgEl.height * (1 - 2 * marginPercent); // vertical
    }

    if (factor !== 1) {
      applyScaleFactor(imgEl, factor, defaults);
      centerCropZone(cropZone, cropBound, defaults);
    }
    return dispatch(ActionCreators.updateScaleFactor(factor));
  };
}

export function initializeCrop(Draggabilly, refs) {
  return dispatch => {
    const cropZone = refs['crop-zone'];
    const cropBound = refs['crop-container'];
    const nwH = refs['nw-handle'];
    const seH = refs['se-handle'];
    const targetImage = refs['target-image'];
    const handleClass = 'handle';

    // Copy into defaults.
    const defaults = {
      top: 0,
      left: 0,
      height: 300,
      width: 300,
    };

    let zoneH = cropZone.offsetHeight || defaults.height;
    let zoneW = cropZone.offsetWidth || defaults.width;
    let zoneT = cropZone.offsetTop || 0;
    let zoneL = cropZone.offsetLeft || 0;

    if (!cropZone.style.top) cropZone.style.top = `${defaults.top}px`;
    if (!cropZone.style.left) cropZone.style.left = `${defaults.left}px`;

    // Prevent huges images overflowing screen.
    dispatch(updateScale(targetImage, cropZone, cropBound, defaults));

    // Instantiate the southeast handle.
    const seh = new Draggabilly(seH, { containment: cropBound });

    seh.on('dragStart', () => {
      dashBorder(cropZone);
      zoneL = Number(cropZone.offsetLeft);
      zoneT = Number(cropZone.offsetTop);
      zoneW = Number(cropZone.offsetWidth);
      zoneH = Number(cropZone.offsetHeight);
    });

    seh.on('dragMove', (event, pointer, moveVector) => {
      let adjX = moveVector.x;
      let adjY = moveVector.y;

      // Square aspect ratio.
      if (Math.abs(adjY) > Math.abs(adjX)) adjY = adjX;
      if (Math.abs(adjX) > Math.abs(adjY)) adjX = adjY;

      // Calculate new dimensions.
      let calcX = zoneW + adjX;
      let calcY = zoneH + adjY;

      // Limit minimum size.
      if (calcX < defaults.width) {
        calcX = defaults.width;
      }
      if (calcY < defaults.height) {
        calcY = defaults.height;
      }

      // Limit maximum size; enforce square aspect ratio.
      const maxX = cropBound.offsetWidth - 2 - zoneL;
      const maxY = cropBound.offsetHeight - 2 - zoneT;
      if (calcX > maxX) {
        calcX = maxX;
        calcY = calcX;
      }
      if (calcY > maxY) {
        calcY = maxY;
        calcX = calcY;
      }

      // Apply new dimensions.
      cropZone.style.width = `${calcX}px`;
      cropZone.style.height = `${calcY}px`;
    });

    seh.on('dragEnd', () => {
      resetHandlePositions(handleClass);
      cropZone.style.borderStyle = cropZone.savedBorder;
    });

    // Instantiate the northwest handle.
    const nwh = new Draggabilly(nwH, { containment: cropBound });

    nwh.on('dragStart', () => {
      dashBorder(cropZone);
      zoneL = Number(cropZone.offsetLeft);
      zoneT = Number(cropZone.offsetTop);
      zoneW = Number(cropZone.offsetWidth);
      zoneH = Number(cropZone.offsetHeight);
    });

    nwh.on('dragMove', (event, pointer, moveVector) => {
      let adjX = moveVector.x;
      let adjY = moveVector.y;

      // Square aspect ratio.
      if (Math.abs(adjY) > Math.abs(adjX)) adjY = adjX;
      if (Math.abs(adjX) > Math.abs(adjY)) adjX = adjY;

      // Calculate new dimensions.
      let calcX = zoneL + adjX;
      let calcY = zoneT + adjY;

      // Prevent from squashing zone.
      const maxX = zoneL + zoneW - defaults.width - 2; // 2px borders...
      const maxY = zoneT + zoneH - defaults.height - 2;
      if (calcX > maxX) {
        calcX = maxX;
      }
      if (calcY > maxY) {
        calcY = maxY;
      }

      if (calcX < 0) {
        calcX = 0;
      }
      if (calcY < 0) {
        calcY = 0;
      }

      // Apply new dimenstions.
      cropZone.style.top = `${calcY}px`;
      cropZone.style.left = `${calcX}px`;
      cropZone.style.height = `${zoneT + zoneH - calcY - 2}px`;
      cropZone.style.width = `${zoneL + zoneW - calcX - 2}px`;
    });

    nwh.on('dragEnd', () => {
      resetHandlePositions(handleClass);
      cropZone.style.borderStyle = cropZone.savedBorder;
    });

    // Create the cropZone.
    const d = new Draggabilly(cropZone, {
      containment: cropBound,
      handle: '.dragger',
    });

    d.on('dragStart', () => {
      nwh.isEnabled = false; // Disable resize handles.
      seh.isEnabled = false; // Disable resize handles.
      cropZone.style.borderStyle = 'dashed';
    });

    d.on('dragEnd', () => {
      resetHandlePositions(handleClass);
      cropZone.style.borderStyle = ''; // Allow CSS to provide default styling.
      nwh.isEnabled = true; // Enable resize hangles.
      seh.isEnabled = true; // Enable resize handles.
    });
  };
}

/**
 * Sends the cropObject to the image service.
 */
export function crop(image, index, refs) {
  const cropZone = refs['crop-zone'];

  return (dispatch, getState) => {
    let { scaleFactor } = getState();

    if (scaleFactor < 1) {
      scaleFactor = 1 / scaleFactor;
    }

    const width = Math.floor(cropZone.clientWidth * scaleFactor);
    const height = Math.floor(cropZone.clientHeight * scaleFactor);
    const top = Math.floor(cropZone.offsetTop * scaleFactor);
    const left = Math.floor(cropZone.offsetLeft * scaleFactor);

    // Call Image Service to crop.
    axios
      .get(
        `/api/validation/image/crop?image=${image}&width=${width}&height=${
          height
        }&top=${top}&left=${left}`,
      )
      .then(response => {
        if (
          response.data.alteration &&
          response.data.alteration === 'cropped' &&
          response.data.image
        ) {
          const uploadedImageObject = {
            url: response.data.image,
            cropped: true,
          };
          dispatch(ActionCreators.addUploadedPhoto(uploadedImageObject, index));
          dispatch(ActionCreators.displayModal(''));
          dispatch(ActionCreators.throwError(''));
        }
      })
      .catch(() => {
        dispatch(ActionCreators.displayModal(''));
        dispatch(ActionCreators.throwError('network-cropper'));
      });
  };
}
